<template>
	<div class="mc-table"
	     :class="[{
      'mc-table--fit': fit,
      'mc-table--striped': stripe,
      'mc-table--border': border || isGroup,
      'mc-table--hidden': isHidden,
      'mc-table--group': isGroup,
      'mc-table--fluid-height': maxHeight,
      'mc-table--scrollable-x': layout.scrollX,
      'mc-table--scrollable-y': layout.scrollY,
      'mc-table--enable-row-hover': !store.states.isComplex,
      'mc-table--enable-row-transition': (store.states.data || []).length !== 0 && (store.states.data || []).length < 100
    }, tableSize ? `mc-table--${ tableSize }` : '']"
	>
		<div class="hidden-columns" ref="hiddenColumns"><slot></slot></div>
		<div class="mc-table__header-wrapper">
			<table-header :store="store"></table-header>
		</div>
		<div class="mc-table__body-wrapper"
		     ref="bodyWrapper"
		     :class="[layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none']"
		     :style="[bodyHeight]">
			<table-body
					:context="context"
					:store="store"
					:stripe="stripe"
					:row-class-name="rowClassName"
					:row-style="rowStyle"
					:highlight="highlightCurrentRow"
					:style="{
	           width: bodyWidth
	        }">
			</table-body>
		</div>
	</div>
</template>

<script>
	import TableHeader from './table-header';
    import debounce from 'throttle-debounce/debounce';
    import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
    import TableStore from './table-store';
    import TableBody from './table-body';
    import TableLayout from './table-layout';

    let tableIdSeed = 1;

    export default {
        name: "McTable",
	    components: {TableHeader, TableBody},
	    props: {
            data: {
                type: Array,
                default: function() {
                    return [];
                }
            },

            size: String,

            width: [String, Number],

            height: [String, Number],

            maxHeight: [String, Number],

            fit: {
                type: Boolean,
                default: true
            },

            stripe: Boolean,

            border: Boolean,

            rowKey: [String, Function],

            context: {},

            showHeader: {
                type: Boolean,
                default: true
            },

            showSummary: Boolean,

            sumText: String,

            summaryMethod: Function,

            rowClassName: [String, Function],

            rowStyle: [Object, Function],

            cellClassName: [String, Function],

            cellStyle: [Object, Function],

            headerRowClassName: [String, Function],

            headerRowStyle: [Object, Function],

            headerCellClassName: [String, Function],

            headerCellStyle: [Object, Function],

            highlightCurrentRow: Boolean,

            currentRowKey: [String, Number],

            emptyText: String,

            expandRowKeys: Array,

            defaultExpandAll: Boolean,

            defaultSort: Object,

            tooltipEffect: String,

            spanMethod: Function,

            selectOnIndeterminate: {
                type: Boolean,
                default: true
            }
        },
	    data(){
            const store = new TableStore(this, {
                rowKey: this.rowKey,
                defaultExpandAll: this.defaultExpandAll,
                selectOnIndeterminate: this.selectOnIndeterminate
            });

            const layout = new TableLayout({
                store,
                table: this,
                fit: this.fit,
                showHeader: this.showHeader
            });
            return {
                store,
                layout,
                isHidden: false,
                renderExpanded: null,
                resizeProxyVisible: false,
                resizeState: {
                    width: null,
                    height: null
                },
                // 是否拥有多级表头
                isGroup: false,
                scrollPosition: 'left'
            }
	    },
        watch: {
            height: {
                immediate: true,
                handler(value) {
                    this.layout.setHeight(value);
                }
            },

            maxHeight: {
                immediate: true,
                handler(value) {
                    this.layout.setMaxHeight(value);
                }
            },

            currentRowKey(newVal) {
                this.store.setCurrentRowKey(newVal);
            },

            data: {
                immediate: true,
                handler(value) {
                    this.store.commit('setData', value);
                    if (this.$ready) {
                        this.$nextTick(() => {
                            this.doLayout();
                        });
                    }
                }
            },

            expandRowKeys: {
                immediate: true,
                handler(newVal) {
                    if (newVal) {
                        this.store.setExpandRowKeys(newVal);
                    }
                }
            }
        },
        computed: {
            tableSize() {
                return this.size || (this.$ELEMENT || {}).size;
            },

            bodyWrapper() {
                return this.$refs.bodyWrapper;
            },

            shouldUpdateHeight() {
                return this.height ||
                    this.maxHeight ||
                    this.fixedColumns.length > 0 ||
                    this.rightFixedColumns.length > 0;
            },

            selection() {
                return this.store.states.selection;
            },

            columns() {
                return this.store.states.columns;
            },

            tableData() {
                return this.store.states.data;
            },

            fixedColumns() {
                return this.store.states.fixedColumns;
            },

            rightFixedColumns() {
                return this.store.states.rightFixedColumns;
            },

            bodyWidth() {
                const { bodyWidth, scrollY, gutterWidth } = this.layout;
                return bodyWidth ? bodyWidth - (scrollY ? gutterWidth : 0) + 'px' : '';
            },

            bodyHeight() {
                if (this.height) {
                    return {
                        height: this.layout.bodyHeight ? this.layout.bodyHeight + 'px' : ''
                    };
                } else if (this.maxHeight) {
                    return {
                        'max-height': (this.showHeader
                            ? this.maxHeight - this.layout.headerHeight - this.layout.footerHeight
                            : this.maxHeight - this.layout.footerHeight) + 'px'
                    };
                }
                return {};
            },

            fixedBodyHeight() {
                if (this.height) {
                    return {
                        height: this.layout.fixedBodyHeight ? this.layout.fixedBodyHeight + 'px' : ''
                    };
                } else if (this.maxHeight) {
                    let maxHeight = this.layout.scrollX ? this.maxHeight - this.layout.gutterWidth : this.maxHeight;

                    if (this.showHeader) {
                        maxHeight -= this.layout.headerHeight;
                    }

                    maxHeight -= this.layout.footerHeight;

                    return {
                        'max-height': maxHeight + 'px'
                    };
                }

                return {};
            },

            fixedHeight() {
                if (this.maxHeight) {
                    if (this.showSummary) {
                        return {
                            bottom: 0
                        };
                    }
                    return {
                        bottom: (this.layout.scrollX && this.data.length) ? this.layout.gutterWidth + 'px' : ''
                    };
                } else {
                    if (this.showSummary) {
                        return {
                            height: this.layout.tableHeight ? this.layout.tableHeight + 'px' : ''
                        };
                    }
                    return {
                        height: this.layout.viewportHeight ? this.layout.viewportHeight + 'px' : ''
                    };
                }
            }
        },
        created() {
            this.tableId = 'mc-table_' + tableIdSeed++;
            this.debouncedUpdateLayout = debounce(50, () => this.doLayout());
        },
	    mounted(){
            this.bindEvents();
            this.store.updateColumns();
            this.doLayout();
            this.$ready = true;

            this.resizeState = {
                width: this.$el.offsetWidth,
                height: this.$el.offsetHeight
            };

            console.log({states: this.store.states})
            // init filters
            this.store.states.columns.forEach(column => {
                if (column.filteredValue && column.filteredValue.length) {
                    this.store.commit('filterChange', {
                        column,
                        values: column.filteredValue,
                        silent: true
                    });
                }
            });

        },
        destroyed() {
            if (this.resizeListener) removeResizeListener(this.$el, this.resizeListener);
        },
        methods: {
            getMigratingConfig() {
                return {
                    events: {
                        expand: 'expand is renamed to expand-change'
                    }
                };
            },

            setCurrentRow(row) {
                this.store.commit('setCurrentRow', row);
            },

            toggleRowSelection(row, selected) {
                this.store.toggleRowSelection(row, selected);
                this.store.updateAllSelected();
            },

            toggleRowExpansion(row, expanded) {
                this.store.toggleRowExpansion(row, expanded);
            },

            clearSelection() {
                this.store.clearSelection();
            },

            clearFilter() {
                this.store.clearFilter();
            },

            clearSort() {
                this.store.clearSort();
            },

            handleMouseLeave() {
                this.store.commit('setHoverRow', null);
                if (this.hoverState) this.hoverState = null;
            },

            updateScrollY() {
                this.layout.updateScrollY();
                this.layout.updateColumnsWidth();
            },

            handleFixedMousewheel(event, data) {
                const bodyWrapper = this.bodyWrapper;
                if (Math.abs(data.spinY) > 0) {
                    const currentScrollTop = bodyWrapper.scrollTop;
                    if (data.pixelY < 0 && currentScrollTop !== 0) {
                        event.preventDefault();
                    }
                    if (data.pixelY > 0 && bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop) {
                        event.preventDefault();
                    }
                    bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5);
                } else {
                    bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5);
                }
            },

            handleHeaderFooterMousewheel(event, data) {
                const { pixelX, pixelY } = data;
                if (Math.abs(pixelX) >= Math.abs(pixelY)) {
                    event.preventDefault();
                    this.bodyWrapper.scrollLeft += data.pixelX / 5;
                }
            },

            bindEvents() {
                const { headerWrapper, footerWrapper } = this.$refs;
                const refs = this.$refs;
                let self = this;

                this.bodyWrapper.addEventListener('scroll', function() {
                    if (headerWrapper) headerWrapper.scrollLeft = this.scrollLeft;
                    if (footerWrapper) footerWrapper.scrollLeft = this.scrollLeft;
                    if (refs.fixedBodyWrapper) refs.fixedBodyWrapper.scrollTop = this.scrollTop;
                    if (refs.rightFixedBodyWrapper) refs.rightFixedBodyWrapper.scrollTop = this.scrollTop;
                    const maxScrollLeftPosition = this.scrollWidth - this.offsetWidth - 1;
                    const scrollLeft = this.scrollLeft;
                    if (scrollLeft >= maxScrollLeftPosition) {
                        self.scrollPosition = 'right';
                    } else if (scrollLeft === 0) {
                        self.scrollPosition = 'left';
                    } else {
                        self.scrollPosition = 'middle';
                    }
                });

                if (this.fit) {
                    addResizeListener(this.$el, this.resizeListener);
                }
            },

            resizeListener() {
                if (!this.$ready) return;
                let shouldUpdateLayout = false;
                const el = this.$el;
                const { width: oldWidth, height: oldHeight } = this.resizeState;

                const width = el.offsetWidth;
                if (oldWidth !== width) {
                    shouldUpdateLayout = true;
                }

                const height = el.offsetHeight;
                if ((this.height || this.shouldUpdateHeight) && oldHeight !== height) {
                    shouldUpdateLayout = true;
                }

                if (shouldUpdateLayout) {
                    this.resizeState.width = width;
                    this.resizeState.height = height;
                    this.doLayout();
                }
            },

            doLayout() {
                console.log('doLayout')
                this.layout.updateColumnsWidth();

                if (this.shouldUpdateHeight) {
                    this.layout.updateElsHeight();
                }
            },

            sort(prop, order) {
                this.store.commit('sort', { prop, order });
            }
        }

    }
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/table/table.vue